import React from "react";
import { learnerCountDisplay } from "../../../../utils/learnerCount";
import { Link } from "react-router-dom";
import { Pricing } from "../../../../stores/PricingStore/Types";

// The three doors side by side: one course, a year of every course, every
// course for good.
//
// Three cards: heading, price, two lines of terms, four ticks, button. The
// middle one is the one we want chosen, and it is the only one with a fill, a
// badge and a solid button. Lifetime sits on the right as the premium anchor
// that makes a year of the Pass look like the sensible choice.
//
// Every card leads with the full price. The monthly way of paying is still
// there, smaller, with what it adds up to, so nobody has to work it out.
// Words under the price are kept to the fewest that answer "will I be
// charged again?": "one-off" or "a year", never "paid" or "payment" (Chris,
// 29 Oct 2026). Until October 2026 the cards led with the monthly figure and the
// Community Hub had the left-hand card; nobody bought the Hub, and the
// monthly framing did not lift sales. There is still no switch: every button
// opens the checkout on paying in full, and spreading the cost is offered
// there.
//
// Side by side on desktop and tablet, stacked on a phone.

type Page = "course" | "home";

interface Props {
  pricing: Pricing;
  page: Page;
  courseName?: string;
  courseSlug?: string;
  compact?: boolean;
  // The pricing page has a header of its own above the grid and keeps notes
  // of its own under it.
  showHeader?: boolean;
  showFooter?: boolean;
}

const Tick: React.FC = () => (
  <span className="pg-tick" role="img" aria-label="Included">
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M3.2 8.6l3.1 3.1 6.6-6.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

// A line under the price. `or` is the other way of paying, set smaller.
type Term = string | { or: string };

interface Head {
  tier: string;
  // The course card names its course; the others leave the slot blank so the
  // three cards keep their prices on one line.
  name?: string;
  // A gold pill above the price, for the one card with a saving worth saying.
  saving?: string;
  amount: string;
  // Always two lines, so every column's heading, price, terms and button
  // stay on the same lines; a card with less to say leaves a line blank.
  terms: [Term, Term];
  featured?: boolean;
}

// One line on a card, for the narrow layouts. `off` is the one thing a card
// lacks, shown muted rather than in red: plainly missing, not alarming.
interface CardLine {
  text: string;
  off?: boolean;
}

interface Column {
  key: "course" | "pass" | "lifetime";
  head: Head;
  button: { label: string; to: string; solid?: boolean };
  lines: CardLine[];
}

const PricingGrid: React.FC<Props> = ({
  pricing,
  page,
  courseName,
  courseSlug,
  compact = false,
  showHeader = true,
  showFooter = true,
}) => {
  const pass = pricing.yearly_pricing;
  const course = pricing.single_course_pricing;
  const lifetime = pricing.lifetime_pricing;

  const symbol = pass.currency_symbol;
  const code = pass.currency_code;
  const courseCount = pricing.course_count || 40;

  const onCoursePage = page === "course";
  // The exact figure the home page shows, falling back to the rounded one.
  const learnerCount = pricing.enrolment_count
    ? `${pricing.enrolment_count.toLocaleString("en-GB")}+`
    : learnerCountDisplay();

  // "or £55 a month for 12 months (£660)": the total always shows, so the
  // spread price is never the smaller-looking number with its cost left for
  // the reader to work out.
  const spread = (
    monthly: string | undefined,
    count: number,
    total: string | undefined,
  ): Term =>
    monthly
      ? {
          or: `or ${symbol}${monthly} a month for ${count} months${
            total ? ` (${symbol}${total})` : ""
          }`,
        }
      : "";

  const months = pass.upfront_months_free;
  const passHead: Head = {
    tier: "All Courses Pass",
    saving: months ? `${months} months free` : undefined,
    amount: pass.amount,
    terms: [
      "a year",
      spread(
        pass.calculated_monthly_amount,
        pass.installment_count || 12,
        pass.monthly_total,
      ),
    ],
    featured: true,
  };

  const courseHead: Head = {
    tier: onCoursePage ? "This course" : "Any one course",
    name: onCoursePage ? courseName || "" : "Your chosen course",
    amount: course.amount,
    terms: [
      "one-off",
      spread(
        course.payment_plan_amount,
        course.payment_plan_installment_count || 3,
        course.payment_plan_total_amount,
      ),
    ],
  };

  // Every button opens the checkout on paying in full; spreading the cost is
  // offered there. On the home page there is no course yet, so the course
  // button opens the checkout that lets you pick one, which starts on paying
  // in full too.
  const courseTo = onCoursePage
    ? course.pay_in_full_checkout_url ||
      `/courses/${courseSlug}/pay?payment_option=full`
    : "/pay/course";
  const passTo = pass.pay_in_full_checkout_url;

  // The same subjects in the same order on every card, so they compare down
  // as well as across: the courses, the certificate, marking, and the Hub's
  // AI tools and research library. The Hub is named by what is in it, which
  // sells better than a community few people use.
  const columns: Column[] = [
    {
      key: "course",
      head: courseHead,
      button: {
        label: onCoursePage ? "Get this course" : "Get a course",
        to: courseTo,
      },
      lines: [
        { text: "Yours for life" },
        { text: "CPD Mini MBA certificate" },
        { text: "Human marking & office hours" },
        { text: "AI tools & research, 3 months" },
      ],
    },
    {
      key: "pass",
      head: passHead,
      button: { label: "Get all courses", to: passTo, solid: true },
      lines: [
        {
          text: onCoursePage
            ? `All ${courseCount}+ courses, this one included`
            : `All ${courseCount}+ courses, pick freely`,
        },
        { text: "Every CPD Mini MBA certificate" },
        { text: "Human marking & office hours" },
        { text: "AI tools & research, all year" },
      ],
    },
  ];

  if (lifetime?.amount) {
    columns.push({
      key: "lifetime",
      head: {
        tier: "Lifetime All Access",
        amount: lifetime.amount,
        terms: ["one-off", ""],
      },
      button: { label: "Get lifetime access", to: lifetime.checkout_url },
      lines: [
        { text: `All ${courseCount}+ courses, and new ones` },
        { text: "Every CPD Mini MBA certificate" },
        { text: "Human marking & office hours" },
        { text: "AI tools & research, for life" },
      ],
    });
  }

  const renderHead = (head: Head) => (
    <>
      {head.featured && <span className="pg-pill">Best value</span>}
      <div className="pg-tier">{head.tier}</div>
      <div className={`pg-name${head.name ? "" : " blank"}`}>
        {head.name || " "}
      </div>
      <div className={`pg-saving-slot${head.saving ? "" : " blank"}`}>
        <span className="pg-saving">{head.saving || " "}</span>
      </div>
      <div className="pg-price">
        <span className="pg-sym">{symbol}</span>
        <span className="pg-amount">{head.amount}</span>
        <span className="pg-ccy">{code}</span>
      </div>
      <div className="pg-terms">
        {head.terms.map((term, index) =>
          typeof term === "string" ? (
            <span key={index} className={term ? undefined : "blank"}>
              {term || " "}
            </span>
          ) : (
            <span key={index} className="pg-or">
              {term.or}
            </span>
          ),
        )}
      </div>
    </>
  );

  // Tells Google Tag Manager which door was chosen and how, so tracking can
  // key on this event rather than on button wording that may change again.
  // Every button now opens on paying in full, so the pay mode is "upfront".
  const announceClick = (column: Column) => {
    const globalWindow = window as any;
    globalWindow.dataLayer = globalWindow.dataLayer || [];
    globalWindow.dataLayer.push({
      event: "pricing_click",
      pricing_option: column.key,
      pricing_pay_mode: "upfront",
      pricing_page: page,
    });
  };

  const renderButton = (column: Column) => (
    <Link
      to={column.button.to}
      className={`btn ${column.button.solid ? "btn-blue" : "btn-outline-blue"}`}
      data-pricing-option={column.key}
      data-pricing-pay-mode="upfront"
      onClick={() => announceClick(column)}
    >
      {column.button.label}
    </Link>
  );

  const giftHref =
    onCoursePage && courseSlug ? `/gift?course=${courseSlug}` : "/gift";

  return (
    <section
      className={`pricing pricing-grid center aligned ${compact ? "pricing--compact" : "pricing--default"}`}
    >
      <div className="container" id="pricing-container">
        {showHeader && (
          <div className="header">
            <p className="pg-kicker">Pricing</p>
            <h2 className="section-title pg-title" id="pricing-title">
              Join{" "}
              <span className="nobr">
                <em className="hl">{learnerCount} happy learners</em>.
              </span>
            </h2>
            <p className="pg-sub">
              One course, a year of every course, or every course for good.
            </p>
          </div>
        )}

        {/* Course, Pass, Lifetime left to right, cheapest first, so the Pass
            sits in the middle. Stacked on a phone the dearest comes first, so
            the first price anyone sees is the anchor (an experiment from
            28 Oct 2026). Not on a course page, where the visitor came for
            that course and it stays on top. */}
        <div className={`pg-cards${onCoursePage ? "" : " pg-cards--dearest-first"}`}>
          {columns.map((column) => (
            <article
              key={column.key}
              className={`pg-card pg-card--${column.key}${column.key === "pass" ? " featured" : ""}`}
            >
              {renderHead(column.head)}
              <ul className="pg-list">
                {column.lines.map((line) => (
                  <li key={line.text} className={line.off ? "off" : undefined}>
                    {line.off ? (
                      <span
                        className="pg-tick off"
                        role="img"
                        aria-label="Not included"
                      >
                        <svg viewBox="0 0 16 16" aria-hidden="true">
                          <path
                            d="M4.5 8h7"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                          />
                        </svg>
                      </span>
                    ) : (
                      <Tick />
                    )}
                    <span>{line.text}</span>
                  </li>
                ))}
              </ul>
              {renderButton(column)}
            </article>
          ))}
        </div>

        {showFooter && (
          <div className="pg-footer">
            <div className="pg-foot-row">
              <span className="pg-foot-item pg-guarantee">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M12 3l7 3v5c0 4.6-3 8.4-7 10-4-1.6-7-5.4-7-10V6l7-3z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9 12l2 2 4-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                30-day money-back guarantee
              </span>
              <span className="pg-foot-item pg-team">
                <span>Buying for a team?</span>{" "}
                <Link to="/team_plans">Teams plan</Link>
              </span>
              <a className="pg-foot-item pg-gift" href={giftHref}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <rect
                    x="3"
                    y="8"
                    width="18"
                    height="5"
                    rx="1.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M5 13v7h14v-7M12 8v12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 8c-1.5-3.5-6-3.5-6-1 0 1.5 3 1 6 1zm0 0c1.5-3.5 6-3.5 6-1 0 1.5-3 1-6 1z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>
                  {onCoursePage ? "Gift this course" : "Gift a course"}
                </span>
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default PricingGrid;
